import React, { useState, useEffect, useCallback, memo, useMemo } from "react";
import {
  Container,
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Person,
  Phone,
  CalendarToday,
  Schedule,
  CheckCircle,
  ArrowBack,
  BusinessCenter,
  WbSunny,
  NightsStay,
  DarkMode,
  LightMode,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useThemeMode } from "../contexts/ThemeContext";
import ModernHeader from "../components/ModernHeader";

// Animated Background Component - Optimized for mobile
const AnimatedBackground = memo(({ mode }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const particleCount = isMobile ? 5 : 15; // Reduce particles on mobile
  
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        overflow: "hidden",
        background: "linear-gradient(135deg, #FFFEF9 0%, #FAF7F2 50%, #FFFEF9 100%)",
        willChange: "auto", // Performance hint
      }}
    >
      {/* Animated Gradient Orbs - Simplified on mobile */}
      {!isMobile && (
        <>
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: mode === "dark" ? [0.3, 0.5, 0.3] : [0.15, 0.25, 0.15],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              position: "absolute",
              top: "20%",
              right: "10%",
              width: "400px",
              height: "400px",
              background: mode === "dark" 
                ? "radial-gradient(circle, rgba(82, 183, 136, 0.4) 0%, transparent 70%)"
                : "radial-gradient(circle, rgba(82, 183, 136, 0.15) 0%, transparent 70%)",
              borderRadius: "50%",
              filter: "blur(60px)",
            }}
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: mode === "dark" ? [0.2, 0.4, 0.2] : [0.1, 0.2, 0.1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
            style={{
              position: "absolute",
              bottom: "20%",
              left: "10%",
              width: "500px",
              height: "500px",
              background: mode === "dark"
                ? "radial-gradient(circle, rgba(149, 213, 178, 0.3) 0%, transparent 70%)"
                : "radial-gradient(circle, rgba(149, 213, 178, 0.12) 0%, transparent 70%)",
              borderRadius: "50%",
              filter: "blur(80px)",
            }}
          />
        </>
      )}

      {/* Floating Particles - Reduced on mobile */}
      {[...Array(particleCount)].map((_, i) => (
        <motion.div
          key={i}
          animate={{
            y: [0, -80, 0],
            x: [0, Math.random() * 40 - 20, 0],
            opacity: mode === "dark" ? [0, 1, 0] : [0, 0.4, 0],
          }}
          transition={{
            duration: Math.random() * 5 + 5,
            repeat: Infinity,
            delay: Math.random() * 5,
          }}
          style={{
            position: "absolute",
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: "2px",
            height: "2px",
            background: mode === "dark" ? "#52B788" : "#95D5B2",
            borderRadius: "50%",
            boxShadow: mode === "dark" ? "0 0 10px #52B788" : "0 0 6px rgba(149, 213, 178, 0.5)",
          }}
        />
      ))}
    </Box>
  );
});
AnimatedBackground.displayName = "AnimatedBackground";

// Motion Components
const MotionBox = motion(Box);
const MotionPaper = motion(Paper);
const MotionGrid = motion(Grid);

export default function BookingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleMode } = useThemeMode();

  const scrollToTop = useCallback(() => window.scrollTo({ top: 0, behavior: "smooth" }), []);
  const goToServices = useCallback(() => navigate("/#services"), [navigate]);
  const goToContact = useCallback(() => navigate("/#contact"), [navigate]);

  // Get service from URL query parameters
  const searchParams = new URLSearchParams(location.search);
  const serviceFromQuery = searchParams.get("service") || "";
  
  // Also check location state for backward compatibility
  const prefilledData = location.state || {};
  const serviceFromState = prefilledData.service || "";
  
  // Prefer query params over state
  const initialService = serviceFromQuery || serviceFromState;

  const HOURS_24 = useMemo(() => ["10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20"], []);
  const deriveMeridian = useCallback((hourStr) => {
    const h = parseInt(hourStr, 10);
    if (Number.isNaN(h)) return "AM";
    return h < 12 ? "AM" : "PM";
  }, []);
  const formatHourLabel = useCallback((hourStr) => {
    const h = parseInt(hourStr, 10);
    if (Number.isNaN(h)) return hourStr;
    const meridian = deriveMeridian(hourStr);
    const hour12 = h > 12 ? h - 12 : h;
    return `${hour12} ${meridian}`;
  }, [deriveMeridian]);
  
  const [form, setForm] = useState({
    name: "",
    contact: "",
    email: "",
    service: initialService,
    date: "",
    hour: "",
    meridian: "AM",
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [pradeepServices, setPradeepServices] = useState([]);
  const [vijayServices, setVijayServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/api/services")
      .then((res) => res.json())
      .then((data) => {
        setPradeepServices(data.pradeep_services || []);
        setVijayServices(data.vijay_services || []);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load services.");
        setLoading(false);
      });
  }, []);

  // Handle prefilled service from query parameters or navigation state
  useEffect(() => {
    const serviceToSet = serviceFromQuery || serviceFromState;
    if (serviceToSet && serviceToSet !== form.service) {
      setForm((prev) => ({ ...prev, service: serviceToSet }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceFromQuery, serviceFromState]);

  const getAllowedHours = useCallback(
    (meridianValue) => (meridianValue === "AM"
      ? ["10", "11", "12"]
      : ["1", "2", "3", "4", "5", "6", "7", "8"]),
    []
  );

  const allowedHours = useMemo(
    () => getAllowedHours(form.meridian),
    [form.meridian, getAllowedHours]
  );

  const handleChange = (e) => {
    if (e.target.name === "hour") {
      const nextMeridian = deriveMeridian(e.target.value);
      setForm({ ...form, hour: e.target.value, meridian: nextMeridian });
      return;
    }

    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDateChange = (newValue) => {
    setSelectedDate(newValue);
    if (newValue) {
      // Format date as YYYY-MM-DD for the form
      const formattedDate = dayjs(newValue).format("YYYY-MM-DD");
      setForm({ ...form, date: formattedDate });
    } else {
      setForm({ ...form, date: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/submit-booking", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json();
        setError(err.error || "Booking failed.");
        setSubmitting(false);
        return;
      }
      const data = await res.json();
      navigate("/confirmation", { state: data });
    } catch {
      setError("Booking failed. Please try again.");
      setSubmitting(false);
    }
  };

  // Detect mobile for performance optimization
  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  }, []);

  // Animation Variants - Simplified on mobile for better performance
  const containerVariants = useMemo(() => ({
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: isMobile ? 0.05 : 0.1,
      },
    },
  }), [isMobile]);

  const itemVariants = useMemo(() => ({
    hidden: { y: isMobile ? 0 : 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: isMobile ? 0.3 : 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  }), [isMobile]);

  const formVariants = useMemo(() => ({
    hidden: { opacity: 0, scale: isMobile ? 1 : 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: isMobile ? 0.4 : 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  }), [isMobile]);

  if (loading) {
    return (
      <>
        <AnimatedBackground mode={mode} />
        <ModernHeader
          mode={mode}
          toggleMode={toggleMode}
          onBookingClick={scrollToTop}
          onServicesClick={goToServices}
          onContactClick={goToContact}
        />
        <Container 
          maxWidth="sm" 
          sx={{ 
            py: 5, 
            position: "relative", 
            zIndex: 1,
            backgroundColor: "#ffffff",
          }}
        >
          <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ minHeight: "60vh" }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  border: "4px solid transparent",
                  borderTopColor: mode === "dark" ? "#52B788" : "#2D6A4F",
                  borderRightColor: mode === "dark" ? "#95D5B2" : "#52B788",
                }}
              />
            </motion.div>
            <Typography
              variant="body1"
              sx={{
                mt: 3,
                color: mode === "dark" ? "#94a3b8" : "#475569",
                fontWeight: 500,
              }}
            >
              Loading services...
            </Typography>
          </MotionBox>
        </Container>
      </>
    );
  }

  return (
    <>
      <AnimatedBackground mode={mode} />
      <ModernHeader
        mode={mode}
        toggleMode={toggleMode}
        onBookingClick={scrollToTop}
        onServicesClick={goToServices}
        onContactClick={goToContact}
      />

      <Container 
        maxWidth="md" 
        sx={{ 
          py: { xs: 3, sm: 4, md: 5 },
          px: { xs: 2, sm: 3 },
          position: "relative", 
          zIndex: 1,
          backgroundColor: "#ffffff",
        }}
      >
        {/* Theme Toggle Button */}
        <Box sx={{ position: "fixed", top: { xs: 12, sm: 16 }, right: { xs: 12, sm: 16 }, zIndex: 1300 }}>
          <Tooltip title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"} arrow>
            <IconButton
              onClick={toggleMode}
              sx={{
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(0, 0, 0, 0.1)",
                color: "#818cf8",
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                "&:hover": {
                  background: "rgba(255, 255, 255, 1)",
                  transform: "scale(1.1)",
                },
                transition: "all 0.3s ease",
              }}
            >
              {mode === "dark" ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Tooltip>
        </Box>

        <MotionPaper
          initial="hidden"
          animate="visible"
          variants={formVariants}
          elevation={0}
          sx={{
            borderRadius: { xs: 4, sm: 6 },
            p: { xs: 2.5, sm: 4, md: 5 },
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
            position: "relative",
            overflow: "hidden",
            mx: { xs: 0, sm: 0 },
          }}
        >
          {/* Animated Header Background */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "200px",
              background: mode === "dark"
                ? "linear-gradient(135deg, rgba(82, 183, 136, 0.1) 0%, rgba(149, 213, 178, 0.1) 100%)"
                : "linear-gradient(135deg, rgba(82, 183, 136, 0.05) 0%, rgba(149, 213, 178, 0.05) 100%)",
              filter: "blur(40px)",
              zIndex: 0,
            }}
          />

          <MotionBox
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            sx={{ position: "relative", zIndex: 1 }}
          >
            {/* Header Badge */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 3,
                py: 1,
                borderRadius: "50px",
                background: mode === "dark" 
                  ? "rgba(82, 183, 136, 0.1)" 
                  : "linear-gradient(135deg, rgba(82, 183, 136, 0.12) 0%, rgba(149, 213, 178, 0.12) 100%)",
                border: mode === "dark" 
                  ? "1px solid rgba(82, 183, 136, 0.3)" 
                  : "1px solid rgba(82, 183, 136, 0.25)",
                mb: 2,
              }}
            >
              <CalendarToday sx={{ fontSize: 16, color: mode === "dark" ? "#52B788" : "#2D6A4F" }} />
              <Typography
                variant="caption"
                sx={{
                  color: mode === "dark" ? "#52B788" : "#2D6A4F",
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                }}
              >
                BOOK YOUR APPOINTMENT
              </Typography>
            </Box>

            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: { xs: 0.75, sm: 1 },
                fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
                background: mode === "dark"
                  ? "linear-gradient(135deg, #2D6A4F 0%, #52B788 50%, #95D5B2 100%)"
                  : "linear-gradient(135deg, #2D6A4F 0%, #52B788 50%, #95D5B2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                px: { xs: 1, sm: 0 },
              }}
            >
              Book Your Service Slot
            </Typography>

            <Typography
              variant="body2"
              align="center"
              sx={{
                color: "#2D6A4F",
                mb: { xs: 2.5, sm: 3 },
                fontSize: { xs: "0.9rem", sm: "1rem" },
                px: { xs: 1, sm: 0 },
              }}
            >
              Fill in your details to schedule a consultation
            </Typography>

            <Divider
              sx={{
                mb: 4,
                background: mode === "dark"
                  ? "linear-gradient(90deg, transparent, rgba(82, 183, 136, 0.5), transparent)"
                  : "linear-gradient(90deg, transparent, rgba(82, 183, 136, 0.4), transparent)",
                height: "2px",
                border: "none",
              }}
            />
          </MotionBox>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    background: mode === "dark" ? "rgba(248, 113, 113, 0.1)" : "rgba(248, 113, 113, 0.15)",
                    border: mode === "dark"
                      ? "1px solid rgba(248, 113, 113, 0.3)"
                      : "1px solid rgba(248, 113, 113, 0.25)",
                    color: mode === "dark" ? "#fca5a5" : "#b91c1c",
                    backgroundImage:
                      mode === "dark"
                        ? "radial-gradient(circle at top, rgba(248,113,113,0.25), transparent)"
                        : "radial-gradient(circle at top, rgba(248,113,113,0.18), transparent)",
                    "& .MuiAlert-icon": {
                      color: mode === "dark" ? "#f87171" : "#dc2626",
                    },
                  }}
                >
                  {error}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          <MotionBox
            component="form"
            onSubmit={handleSubmit}
            autoComplete="on"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            sx={{ position: "relative", zIndex: 1 }}
          >
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {/* Name Field */}
              <MotionGrid item xs={12} variants={itemVariants}>
                <motion.div
                  whileHover={!isMobile ? { scale: 1.01 } : {}}
                  whileTap={!isMobile ? { scale: 0.99 } : {}}
                >
                  <TextField
                    label="Name of Applicant"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    fullWidth
                    autoComplete="name"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Person sx={{ color: mode === "dark" ? "#52B788" : "#2D6A4F" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: "rgba(82, 183, 136, 0.04)",
                        backdropFilter: "blur(10px)",
                        borderRadius: { xs: 2, sm: 3 },
                        fontSize: { xs: "0.95rem", sm: "1rem" },
                        minHeight: { xs: "48px", sm: "56px" },
                        transition: "all 0.3s ease",
                        "&:hover": {
                        background: "rgba(82, 183, 136, 0.06)",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                      },
                    }}
                  />
                </motion.div>
              </MotionGrid>

              {/* Contact Field */}
              <MotionGrid item xs={12} variants={itemVariants}>
                <motion.div
                  whileHover={!isMobile ? { scale: 1.01 } : {}}
                  whileTap={!isMobile ? { scale: 0.99 } : {}}
                >
                  <TextField
                    label="Contact Details"
                    name="contact"
                    value={form.contact}
                    onChange={handleChange}
                    required
                    fullWidth
                    placeholder="+91XXXXXXXXXX"
                    autoComplete="tel"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ color: mode === "dark" ? "#52B788" : "#2D6A4F" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: "rgba(82, 183, 136, 0.04)",
                        backdropFilter: "blur(10px)",
                        borderRadius: { xs: 2, sm: 3 },
                        fontSize: { xs: "0.95rem", sm: "1rem" },
                        minHeight: { xs: "48px", sm: "56px" },
                        transition: "all 0.3s ease",
                        "&:hover": {
                        background: "rgba(82, 183, 136, 0.06)",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                      },
                    }}
                  />
                </motion.div>
              </MotionGrid>

              {/* Email Field */}
              <MotionGrid item xs={12} variants={itemVariants}>
                <motion.div
                  whileHover={!isMobile ? { scale: 1.01 } : {}}
                  whileTap={!isMobile ? { scale: 0.99 } : {}}
                >
                  <TextField
                    label="Email Address"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    fullWidth
                    placeholder="your.email@example.com"
                    autoComplete="email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ color: mode === "dark" ? "#52B788" : "#2D6A4F" }}>@</Typography>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: "rgba(82, 183, 136, 0.04)",
                        backdropFilter: "blur(10px)",
                        borderRadius: { xs: 2, sm: 3 },
                        fontSize: { xs: "0.95rem", sm: "1rem" },
                        minHeight: { xs: "48px", sm: "56px" },
                        transition: "all 0.3s ease",
                        "&:hover": {
                        background: "rgba(82, 183, 136, 0.06)",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: { xs: "0.9rem", sm: "1rem" },
                      },
                    }}
                  />
                </motion.div>
              </MotionGrid>

              {/* Service Selection */}
              <MotionGrid item xs={12} variants={itemVariants}>
                <motion.div
                  whileHover={!isMobile ? { scale: 1.01 } : {}}
                  whileTap={!isMobile ? { scale: 0.99 } : {}}
                >
                  <FormControl fullWidth required>
                    <InputLabel
                      id="service-label"
                      sx={{
                        "&.Mui-focused": {
                          color: mode === "dark" ? "#95D5B2" : "#52B788",
                        },
                      }}
                    >
                      Select Service
                    </InputLabel>
                    <Select
                      labelId="service-label"
                      label="Select Service"
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography sx={{ color: mode === "dark" ? "rgba(255,255,255,0.6)" : "rgba(15,23,42,0.5)" }}>
                            Select Service
                          </Typography>
                        );
                      }
                      return selected;
                    }}
                      startAdornment={
                        <InputAdornment position="start">
                          <BusinessCenter sx={{ color: mode === "dark" ? "#95D5B2" : "#52B788", ml: 1 }} />
                        </InputAdornment>
                      }
                      sx={{
                        background: "rgba(82, 183, 136, 0.04)",
                        backdropFilter: "blur(10px)",
                        borderRadius: 3,
                        transition: "all 0.3s ease",
                        "&:hover": {
                        background: "rgba(82, 183, 136, 0.06)",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            background: "rgba(255, 255, 255, 0.95)",
                            backdropFilter: "blur(20px)",
                            border: "1px solid rgba(149, 213, 178, 0.3)",
                            borderRadius: 2,
                            mt: 1,
                            "& .MuiMenuItem-root": {
                              "&:hover": {
                                background: "rgba(82, 183, 136, 0.2)",
                              },
                              "&.Mui-selected": {
                                background: "rgba(82, 183, 136, 0.3)",
                                "&:hover": {
                                  background: "rgba(82, 183, 136, 0.4)",
                                },
                              },
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem disabled sx={{ color: mode === "dark" ? "#52B788" : "#2D6A4F", fontWeight: 600 }}>
                        Services by A. PRADEEP
                      </MenuItem>
                      {pradeepServices.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                      <Divider sx={{ my: 1, bgcolor: mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)" }} />
                      <MenuItem disabled sx={{ color: mode === "dark" ? "#95D5B2" : "#52B788", fontWeight: 600 }}>
                        Services by N. VIJAYABHASKARA REDDY
                      </MenuItem>
                      {vijayServices.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </motion.div>
              </MotionGrid>

              {/* Date Field */}
              <MotionGrid item xs={12} variants={itemVariants}>
                <motion.div
                  whileHover={!isMobile ? { scale: 1.01 } : {}}
                  whileTap={!isMobile ? { scale: 0.99 } : {}}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Select Date"
                      value={selectedDate}
                      onChange={handleDateChange}
                      minDate={dayjs()}
                      slotProps={{
                        textField: {
                          required: true,
                          fullWidth: true,
                          name: "date",
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarToday sx={{ color: mode === "dark" ? "#52B788" : "#2D6A4F" }} />
                              </InputAdornment>
                            ),
                          },
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              background: mode === "dark" 
                                ? "rgba(82, 183, 136, 0.05)" 
                                : "rgba(82, 183, 136, 0.04)",
                              backdropFilter: "blur(10px)",
                              borderRadius: 3,
                              transition: "all 0.3s ease",
                              "&:hover": {
                        background: "rgba(82, 183, 136, 0.06)",
                              },
                              "& fieldset": {
                                borderColor: "rgba(82, 183, 136, 0.3)",
                                borderWidth: "2px",
                              },
                              "&:hover fieldset": {
                                borderColor: "rgba(82, 183, 136, 0.5)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#52B788",
                                boxShadow: "0 0 20px rgba(82, 183, 136, 0.3)",
                              },
                            },
                            "& .MuiInputLabel-root": {
                              "&.Mui-focused": {
                                color: mode === "dark" ? "#52B788" : "#2D6A4F",
                              },
                            },
                          },
                        },
                        popper: {
                          sx: {
                            "& .MuiPaper-root": {
                              background: "rgba(255, 255, 255, 0.95)",
                              backdropFilter: "blur(20px)",
                              border: mode === "dark" 
                                ? "1px solid rgba(82, 183, 136, 0.3)" 
                                : "1px solid rgba(82, 183, 136, 0.3)",
                              borderRadius: 3,
                              boxShadow: mode === "dark"
                                ? "0 8px 32px rgba(0, 0, 0, 0.4)"
                                : "0 8px 32px rgba(0, 0, 0, 0.15)",
                              "& .MuiPickersCalendarHeader-root": {
                                color: "#000000 !important",
                                "& *": {
                                  color: "#000000 !important",
                                },
                              },
                              "& .MuiDayCalendar-header": {
                                "& .MuiTypography-root": {
                                  color: "#2D6A4F",
                                  fontWeight: 700,
                                },
                              },
                              "& .MuiDayCalendar-weekContainer": {
                                "& .MuiPickersDay-root": {
                                  color: "#1B4D35",
                                  fontWeight: 700,
                                  "&:hover": {
                                    background: mode === "dark" 
                                      ? "rgba(82, 183, 136, 0.2)" 
                                      : "rgba(82, 183, 136, 0.15)",
                                  },
                                  "&.Mui-selected": {
                                    background: mode === "dark"
                                      ? "linear-gradient(135deg, #52B788 0%, #95D5B2 100%)"
                                      : "linear-gradient(135deg, #2D6A4F 0%, #52B788 100%)",
                                    color: "#ffffff",
                                    "&:hover": {
                                      background: mode === "dark"
                                        ? "linear-gradient(135deg, #95D5B2 0%, #52B788 100%)"
                                        : "linear-gradient(135deg, #52B788 0%, #2D6A4F 100%)",
                                    },
                                  },
                                  "&.Mui-disabled": {
                                    color: mode === "dark" ? "#475569" : "#cbd5e1",
                                  },
                                },
                              },
                              "& .MuiPickersCalendarHeader-labelContainer": {
                                "& .MuiPickersCalendarHeader-label": {
                                  color: "#000000 !important",
                                  fontWeight: 700,
                                },
                                "& button": {
                                  color: "#000000 !important",
                                },
                              },
                              "& .MuiPickersCalendarHeader-switchViewButton": {
                                color: "#000000 !important",
                                "& *": {
                                  color: "#000000 !important",
                                },
                              },
                              "& .MuiPickersArrowSwitcher-root": {
                                "& button": {
                                  color: "#2D6A4F !important",
                                },
                              },
                              "& .MuiIconButton-root": {
                                color: "#2D6A4F",
                                fontWeight: 700,
                                "&:hover": {
                                  background: "rgba(82, 183, 136, 0.15)",
                                  color: "#1B4D35",
                                },
                              },
                              "& .MuiPickersCalendarHeader-label": {
                                color: "#000000 !important",
                                fontWeight: 700,
                              },
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </motion.div>
              </MotionGrid>

              {/* Time Selection */}
              <MotionGrid item xs={12} sm={6} variants={itemVariants}>
                <motion.div
                  whileHover={!isMobile ? { scale: 1.01 } : {}}
                  whileTap={!isMobile ? { scale: 0.99 } : {}}
                >
                  <FormControl fullWidth required>
                    <InputLabel
                      id="hour-label"
                      sx={{
                        "&.Mui-focused": {
                          color: mode === "dark" ? "#95D5B2" : "#52B788",
                        },
                      }}
                    >
                      Time
                    </InputLabel>
                    <Select
                      labelId="hour-label"
                      label="Time"
                      name="hour"
                      value={form.hour}
                      onChange={handleChange}
                      startAdornment={
                        form.hour ? (
                          <InputAdornment position="start">
                            {deriveMeridian(form.hour) === "AM" ? (
                              <WbSunny sx={{ color: mode === "dark" ? "#fbbf24" : "#f59e0b", ml: 1 }} />
                            ) : (
                              <NightsStay sx={{ color: mode === "dark" ? "#818cf8" : "#6366f1", ml: 1 }} />
                            )}
                          </InputAdornment>
                        ) : null
                      }
                      sx={{
                        background: "rgba(82, 183, 136, 0.04)",
                        backdropFilter: "blur(10px)",
                        borderRadius: 3,
                        transition: "all 0.3s ease",
                        "&:hover": {
                        background: "rgba(82, 183, 136, 0.06)",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            background: "rgba(255, 255, 255, 0.95)",
                            backdropFilter: "blur(20px)",
                            border: "1px solid rgba(82, 183, 136, 0.3)",
                            borderRadius: 2,
                            mt: 1,
                            maxHeight: 300,
                            "& .MuiMenuItem-root": {
                              "&:hover": {
                                background: "rgba(82, 183, 136, 0.2)",
                              },
                              "&.Mui-selected": {
                                background: "rgba(82, 183, 136, 0.3)",
                                "&:hover": {
                                  background: "rgba(82, 183, 136, 0.4)",
                                },
                              },
                            },
                          },
                        },
                      }}
                    >
                    {HOURS_24.map((hour) => (
                      <MenuItem key={hour} value={hour}>
                        {formatHourLabel(hour)}
                      </MenuItem>
                    ))}
                    </Select>
                  </FormControl>
                </motion.div>
              </MotionGrid>

              {/* Submit Button */}
              <MotionGrid item xs={12} variants={itemVariants}>
                <motion.div
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={submitting}
                    startIcon={submitting ? null : <CheckCircle />}
                    sx={{
                      mt: { xs: 1.5, sm: 2 },
                      py: { xs: 1.75, sm: 2 },
                      borderRadius: { xs: 2, sm: 3 },
                      fontSize: { xs: "1rem", sm: "1.1rem" },
                      fontWeight: 700,
                      minHeight: { xs: "48px", sm: "56px" },
                      background: mode === "dark"
                        ? "linear-gradient(135deg, #52B788 0%, #95D5B2 100%)"
                        : "linear-gradient(135deg, #2D6A4F 0%, #52B788 100%)",
                      color: "white",
                      border: mode === "dark"
                        ? "2px solid rgba(255, 255, 255, 0.1)"
                        : "2px solid rgba(59, 130, 246, 0.2)",
                      boxShadow: mode === "dark"
                        ? "0 8px 30px rgba(82, 183, 136, 0.4)"
                        : "0 12px 32px rgba(45, 106, 79, 0.25)",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "-100%",
                        width: "100%",
                        height: "100%",
                        background:
                          "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                        transition: "left 0.5s",
                      },
                      "&:hover": {
                        background: mode === "dark"
                          ? "linear-gradient(135deg, #95D5B2 0%, #52B788 100%)"
                          : "linear-gradient(135deg, #52B788 0%, #2D6A4F 100%)",
                        boxShadow: mode === "dark"
                          ? "0 12px 40px rgba(82, 183, 136, 0.6)"
                          : "0 16px 36px rgba(45, 106, 79, 0.35)",
                        "&::before": {
                          left: "100%",
                        },
                      },
                      "&:disabled": {
                        background: mode === "dark" ? "rgba(148, 163, 184, 0.2)" : "rgba(148, 163, 184, 0.18)",
                        color: mode === "dark" ? "#94a3b8" : "#64748b",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {submitting ? (
                      <CircularProgress size={24} sx={{ color: mode === "dark" ? "#cbd5f5" : "#1e293b" }} />
                    ) : (
                      "Book Slot"
                    )}
                  </Button>
                </motion.div>
              </MotionGrid>

              {/* Back Button */}
              <MotionGrid item xs={12} variants={itemVariants}>
                <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                  <Button
                    onClick={() => navigate("/")}
                    variant="outlined"
                    fullWidth
                    startIcon={<ArrowBack />}
                    sx={{
                      py: { xs: 1.25, sm: 1.5 },
                      borderRadius: { xs: 2, sm: 3 },
                      borderColor: mode === "dark" ? "rgba(148, 163, 184, 0.3)" : "rgba(59, 130, 246, 0.3)",
                      color: mode === "dark" ? "#94a3b8" : "#2563eb",
                      fontWeight: 600,
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                      minHeight: { xs: "44px", sm: "48px" },
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: mode === "dark" ? "#52B788" : "#2563eb",
                        color: mode === "dark" ? "#52B788" : "#1d4ed8",
                        background: mode === "dark"
                          ? "rgba(82, 183, 136, 0.05)"
                          : "rgba(37, 99, 235, 0.08)",
                        boxShadow: mode === "dark"
                          ? "0 4px 20px rgba(82, 183, 136, 0.2)"
                          : "0 10px 24px rgba(37, 99, 235, 0.2)",
                      },
                    }}
                  >
                    Back to Home
                  </Button>
                </motion.div>
              </MotionGrid>
            </Grid>
          </MotionBox>

          {/* Decorative Elements */}
          <Box
            sx={{
              position: "absolute",
              bottom: 20,
              right: 20,
              opacity: 0.1,
              pointerEvents: "none",
            }}
          >
            <motion.div
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear",
              }}
            >
              <CalendarToday sx={{ fontSize: 120, color: mode === "dark" ? "#52B788" : "#2D6A4F" }} />
            </motion.div>
          </Box>
        </MotionPaper>

        {/* Info Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <Box
            sx={{
              mt: 3,
              p: 3,
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
              border: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
              textAlign: "center",
            }}
          >
            <Typography variant="body2" sx={{ color: mode === "dark" ? "#94a3b8" : "#4a5568", fontStyle: "italic" }}>
              Your information is secure • We'll confirm your booking shortly
            </Typography>
            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "center",
                gap: 1,
              }}
            >
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.3, 1, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.3,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #52B788, #95D5B2)",
                    }}
                  />
                </motion.div>
              ))}
            </Box>
          </Box>
        </motion.div>
      </Container>
    </>
  );
}
